import React, {Fragment, useEffect, useState, useRef} from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';

import { Canvas, useLoader } from "@react-three/fiber";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { GLTFExporter } from "three/examples/jsm/exporters/GLTFExporter.js";

import { Button, TextField, Grid, Typography, useTheme, Dialog, DialogTitle, DialogContent, LinearProgress } from '@mui/material';
import { AddCircle, ContentCopy, Delete, OpenInBrowser } from "@mui/icons-material";
import Download from '@mui/icons-material/DownloadForOffline';

import apiClient from '../../../../services/api';
import store from '../../../../store/index';
import { toggleMessageOpen } from '../../../../store/actions/Message';
import { setRegenerateUsdz, setSelectedProject } from "../../../../store/actions/Project";
import { createConfirmAction } from "../../../../store/actions/ConfirmDialog";

const GLTF = (props) => {
    const { url, setLoadingTitle, setDialogOpen, project } = props;

    const modelRef = useRef();
    const { t } = useTranslation();
    const gltf = useLoader(GLTFLoader, url);
    let started = false;

    useEffect(() => {
        if(!gltf) {
            return;
        }

        convertGltfToGlb(gltf.scene);
    }, [gltf]);

    const upload = (file, id, t) => {
        console.log('upload called');
        const headers = {'Content-Type': 'multipart/form-data'};
        const formData = new FormData();

        formData.append('id', id);
        formData.append('file', file);
        formData.append('isOptimized', true);

        apiClient.post('/api/projects/generate-optimized-public-url', formData, {headers}).then((response) => {
            if(response.data.success) {
                console.log(response.data);
                setDialogOpen(false);
                store.dispatch(toggleMessageOpen(t('File saved successfully'), 'success'));
                store.dispatch(setSelectedProject(response.data));
            } else {
                store.dispatch(toggleMessageOpen(response.data.message, 'error'));
            }
        }).catch(error => {
            console.log(error.response);
            store.dispatch(toggleMessageOpen(t('An error ocurred while saving file'), 'error'));
        });
    }

    /*
    data.message
    */

    async function convertGltfToGlb(scene) {
        if (!scene || started) return;

        started = true;
        const exporter = new GLTFExporter();
        const options = { binary: true};

        setLoadingTitle(t('Convert GLTF to GLB'));
        
        const model = gltf.scene;

        /*
        // Traverse through all children in the model
        gltf.scene.traverse((object) => {
            // Check if the object is a Mesh
            if (object.isMesh) {
                const mesh = object;

                // Get the material(s) of the mesh
                const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];

                // Remove textures from each material
                materials.forEach((material) => {
                    if (material.map) material.map = null;  // Remove the diffuse map
                    if (material.aoMap) material.aoMap = null;  // Remove the ambient occlusion map
                    if (material.emissiveMap) material.emissiveMap = null;  // Remove the emissive map
                    if (material.bumpMap) material.bumpMap = null;  // Remove the bump map
                    if (material.normalMap) material.normalMap = null;  // Remove the normal map
                    if (material.displacementMap) material.displacementMap = null;  // Remove the displacement map
                    if (material.roughnessMap) material.roughnessMap = null;  // Remove the roughness map
                    if (material.metalnessMap) material.metalnessMap = null;  // Remove the metalness map
                    if (material.alphaMap) material.alphaMap = null;  // Remove the alpha map
                    if (material.envMap) material.envMap = null;  // Remove the environment map
                    if (material.clearcoatNormalMap) material.clearcoatNormalMap = null;  // Remove the clearcoat normal map
                    if (material.clearcoatRoughnessMap) material.clearcoatRoughnessMap = null;  // Remove the clearcoat roughness map
                    if (material.iridescenceMap) material.iridescenceMap = null;  // Remove the iridescence map
                    if (material.sheenColorMap) material.sheenColorMap = null;  // Remove the sheen color map
                    if (material.sheenRoughnessMap) material.sheenRoughnessMap = null;  // Remove the sheen roughness map
                    
                    material.needsUpdate = true;  // Mark the material as needing an update
                });
            }
        });
        */

        exporter.parse(
            scene,
            function ( gltf ) {

                const blob = new Blob([gltf], { type: 'application/otcet-stream' });

                console.log(blob);
               
                upload(blob, project.id, t, true);
            },
            function ( error ) { console.log( 'An error happened', error ); },
            options
        );
    }

    return (
        <group ref={modelRef}>
            <primitive object={gltf.scene} />
        </group>
    );
}

export default function PublicTab()
{
    const { t } = useTranslation();
    const theme = useTheme();
    const ref = useRef();

    const selectedProject = useSelector((state) => state.project.selectedProject);
    const createGLBAutomatically = useSelector((state) => state.project.regenerateUsdz);

    const [project, setProject] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [loadingTitle, setLoadingTitle] = useState('');
    const [model, setModel] = useState(null);
    const [generateStarted, setGenerateStarted] = useState(false);

    useEffect(() => {
        if(selectedProject) {
            setProject(selectedProject.project)
        }
    }, [selectedProject])

    const handleCreateLink = () => {
        apiClient.post(
            '/api/projects/create-public-url',
            {id: project.id, origin: window.location.origin}
        ).then( response => {
            store.dispatch(setSelectedProject(response.data));
        }).catch( error => store.dispatch(toggleMessageOpen(error, "error")) );
    }

    const handleCopy = (url) => {
        navigator.clipboard.writeText(url);

        store.dispatch(toggleMessageOpen(t('Text copied to clipboard'), "success"));
    }

    const handleOpen = (url) => { window.open(url) }

    const handleDelete = (type) => {
        const isOptimized = (type != 'default' ? true : false);

        apiClient.post('/api/projects/delete-public-url', {id: project.id, isOptimized: isOptimized}).then(response => {
            store.dispatch(setSelectedProject(response.data));
        }).catch(error => store.dispatch(toggleMessageOpen(error, "error")));
    }

    const handleDownloadQrCode = () => {
        const image = document.getElementById('qr-code');
        let anchor = document.createElement('a');

        anchor.setAttribute('href', image.getAttribute('src'));
        anchor.setAttribute('download', 'qr-code');
        document.body.appendChild(anchor);

        anchor.click();
        anchor.remove();
    }

    const handleGenerateOptimized = () => {
        setDialogOpen(true);
        setLoadingTitle(t('Resizing textures'));

        apiClient.post('/api/projects/resize-textures', {id: project.id}).then(response => {
            setModel(response.data.model);
        }).catch(error => store.dispatch(toggleMessageOpen(error, "error")));
    }

    const PublicURL = (props) => {
        const { type, title } = props;
        const token = type === 'default' ? project?.public_view_token : project?.public_view_token_optimized;
        const qrCode = type === 'default' ? project.qr_code : project.qr_code_optimized;
        const url = window.location.origin + '/public/project/view/' + token;

        return (
            <Fragment>
                <Typography style={{fontWeight: 'bold'}}>
                    {title}
                </Typography>
                <TextField
                    multiline
                    fullWidth
                    value={url}
                    readonly
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: theme.palette.border.primary,
                            },
                            '&:hover fieldset': {
                                borderColor: theme.palette.border.secondary,
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: theme.palette.border.secondary,
                            }
                        }
                    }}
                />
                <Grid container spacing={2} style={{marginTop: '10px', marginBottom: '10px'}}>
                    <Grid item xs={4}>
                        <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            onClick={() => {handleCopy(url)}}
                            startIcon={<ContentCopy/>}
                        >   
                            {t('Copy')}
                        </Button>
                    </Grid>
                    <Grid item xs={4}>
                        <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            onClick={() => {handleOpen(url)}}
                            startIcon={<OpenInBrowser/>}
                        >
                            {t('Open')}
                        </Button>
                    </Grid>
                    <Grid item xs={4}>
                        <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            onClick={() => store.dispatch(createConfirmAction(() => handleDelete(type), t('Are you sure?')))}
                            startIcon={<Delete/>}
                        >
                            {t('Delete')}
                        </Button>
                    </Grid>
                </Grid>

                <div style={{textAlign: 'center'}}>
                    <div className="qr-code-container" onClick={handleDownloadQrCode}>
                        <img src={qrCode} id="qr-code" style={{width: '200px', height: '200px'}} alt="" />
                        <div className="qr-code-download">
                            <Download style={{color: 'green', position: 'absolute', top: 'calc(50% - 12px)', right: 'calc(50% - 14px)'}}/>
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    }

    return (
        <React.Fragment>
            {
                (!project || !project.public_view_token)
                ? 
                    <Button
                        variant="contained"
                        onClick={handleCreateLink}
                        fullWidth
                        size='small'
                        startIcon={<AddCircle />}
                        style={{marginBottom: '5px'}}
                    >
                        {t('Create Public Link')}
                    </Button>
                :
                    <div style={{marginTop: '10px', display: (project.public_view_token === null ? 'none' : 'block')}}>
                        <PublicURL type='default' title={t('Created public link')} />
                    </div>
            }
            {
                /*
                (!project || !project.public_view_token_optimized)
                ? 
                    <Button
                        variant="contained"
                        onClick={handleGenerateOptimized}
                        fullWidth
                        size='small'
                        startIcon={<AddCircle />}
                    >
                        {t('Generate optimized Public Link')}
                    </Button>
                :
                    <div style={{marginTop: '10px', display: (project.public_view_token === null ? 'none' : 'block')}}>
                        <PublicURL type='optimized' title={t('Created optimized public link')} />
                    </div>
                */
            }
            <Dialog open={dialogOpen}>
                <DialogTitle style={{fontWeight: 'bold'}}>
                    {loadingTitle}
                </DialogTitle>
                <DialogContent>
                    <LinearProgress />
                    <Canvas>
                        {
                            model && <GLTF
                                url={model}
                                setLoadingTitle={setLoadingTitle}
                                setDialogOpen={setDialogOpen}
                                project={project}
                            />
                        }
                    </Canvas>
                </DialogContent>
            </Dialog>
        </React.Fragment>
    )
}